import React from 'react';
import {
  CheckCircle2,
  Circle,
  CircleDashed,
  Loader2,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import type {
  DemoAgencySetupStepStatus,
  DemoEnvironmentResponse,
  DemoEnvironmentRunResponse,
} from '../../services/api/types.gen';
import { formatDateInPT } from '../../utils/dateUtils';
import {
  STEP_LABELS,
  displaySteps,
  isWorthShowing,
  messageParts,
} from './demoRunSteps';

/**
 * One agency's current or last run, shown when its row is expanded.
 *
 * The run comes from the list, which polls while it is running, so the
 * checklist fills in live.
 */

interface Props {
  environment: DemoEnvironmentResponse;
}

export const DemoEnvironmentRunDetails: React.FC<Props> = ({ environment }) => {
  const latest = environment.latest_run ?? null;

  if (!latest) {
    return (
      <p className='text-sm text-muted-foreground'>
        No runs yet. Run it to set this sandbox up.
      </p>
    );
  }

  // The current run, or the last one. Earlier runs are not shown: a sandbox is
  // rebuilt from scratch every time, so the run before this one describes a
  // sandbox that no longer exists.
  return <RunChecklist run={latest} />;
};

const STEP_ICONS: Record<
  DemoAgencySetupStepStatus,
  { Icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  succeeded: { Icon: CheckCircle2, className: 'text-green-600' },
  failed: { Icon: XCircle, className: 'text-red-600' },
  skipped: { Icon: MinusCircle, className: 'text-muted-foreground' },
  pending: { Icon: Circle, className: 'text-muted-foreground/50' },
  not_implemented: { Icon: CircleDashed, className: 'text-muted-foreground' },
};

/**
 * One row per step: status icon and label on the left, the message on the right.
 * Messages pack several results into one string, so each part gets its own line.
 */
/**
 * One card per step: the status and what it is called, with the counts small
 * underneath. Two columns, because ten steps in one narrow list made every
 * card look like a log line and none of them look like a heading.
 */
const RunChecklist: React.FC<{ run: DemoEnvironmentRunResponse }> = ({
  run,
}) => {
  const steps = displaySteps(run).filter(isWorthShowing);
  // The first pending step of a running run is the one in progress.
  const currentId =
    run.status === 'running'
      ? steps.find(step => step.status === 'pending')?.id
      : undefined;
  return (
    /* CSS columns rather than a grid, because the order has to run DOWN a column
       and then across: the steps are a sequence, and a grid lays them out left to
       right, so step two sat beside step one and the run read as though it had
       started in three places at once. `break-inside` keeps a step's message with
       its own label. */
    <ul
      aria-label={`Steps for the run started ${formatDateInPT(run.started_at)}`}
      className='columns-1 gap-x-8 py-1 sm:columns-2 lg:columns-3'
    >
      {steps.map(step => {
        const isCurrent = step.id === currentId;
        const { Icon, className } = STEP_ICONS[step.status];
        const pending = step.status === 'pending' && !isCurrent;
        return (
          <li
            key={step.id}
            className='flex break-inside-avoid items-start gap-2 pb-3'
          >
            {isCurrent ? (
              <Loader2 className='mt-0.5 h-4 w-4 shrink-0 animate-spin text-blue-600' />
            ) : (
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${className}`} />
            )}
            <span className='sr-only'>
              {isCurrent ? 'running' : step.status}
            </span>
            <div className='min-w-0'>
              <div
                className={`text-sm font-medium ${pending ? 'text-muted-foreground' : ''}`}
              >
                {STEP_LABELS[step.id] ?? step.id}
              </div>
              {messageParts(step.message).map((part, index) => (
                <div
                  key={index}
                  className='break-words text-xs text-muted-foreground'
                >
                  {part.label && <span>{part.label}: </span>}
                  {part.text}
                </div>
              ))}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default DemoEnvironmentRunDetails;
